/**
 * Store Menu Service
 * Handles filtering and fetching menu items, categories, modifiers, etc. by store ID
 */
import { firestore } from '../../../../../libs/firebase-service/src';

/**
 * Fetch all categories for a specific store
 * @param {string} storeId - The store ID to filter by
 * @returns {Promise<Array>} Array of categories for the store
 */
export const getCategoriesByStore = async (storeId) => {
  try {
    if (!storeId) {
      throw new Error('Store ID is required');
    }

    const categoriesSnapshot = await firestore
      .collection('categories')
      .where('store_id', '==', storeId)
      .where('is_deleted', '==', false)
      .orderBy('order', 'asc')
      .get();

    const categories = categoriesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));


    return categories;
  } catch (error) {
    console.error('Error fetching categories by store:', error);
    throw error;
  }
};

/**
 * Fetch all menu items for a specific store
 * @param {string} storeId - The store ID to filter by
 * @param {string} categoryId - Optional category ID to filter further
 * @returns {Promise<Array>} Array of menu items for the store
 */
export const getMenuItemsByStore = async (storeId, categoryId = null) => {
  try {
    if (!storeId) {
      throw new Error('Store ID is required');
    }

    let query = firestore
      .collection('menuItems')
      .where('store_id', '==', storeId)
      .where('is_deleted', '==', false);

    if (categoryId) {
      query = query.where('category_id', '==', categoryId);
    }

    const menuItemsSnapshot = await query
      .orderBy('order', 'asc')
      .get();

    const menuItems = menuItemsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return menuItems;
  } catch (error) {
    console.error('Error fetching menu items by store:', error);
    throw error;
  }
};

/**
 * Fetch all modifiers for a specific store
 * @param {string} storeId - The store ID to filter by
 * @returns {Promise<Array>} Array of modifiers for the store
 */
export const getModifiersByStore = async (storeId) => {
  try {
    if (!storeId) {
      throw new Error('Store ID is required');
    }

    const modifiersSnapshot = await firestore
      .collection('modifiers')
      .where('store_id', '==', storeId)
      .where('is_deleted', '==', false)
      .get();

    const modifiers = modifiersSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

 
    return modifiers;
  } catch (error) {
    console.error('Error fetching modifiers by store:', error);
    throw error;
  }
};

/**
 * Fetch all discounts for a specific store
 * @param {string} storeId - The store ID to filter by
 * @returns {Promise<Array>} Array of discounts for the store
 */
export const getDiscountsByStore = async (storeId) => {
  try {
    if (!storeId) {
      throw new Error('Store ID is required');
    }

    const discountsSnapshot = await firestore
      .collection('discounts')
      .where('store_id', '==', storeId)
      .where('is_active', '==', true)
      .get();

    const discounts = discountsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return discounts;
  } catch (error) {
    console.error('Error fetching discounts by store:', error);
    throw error;
  }
};

/**
 * Fetch complete menu data for a specific store
 * @param {string} storeId - The store ID to fetch menu for
 * @returns {Promise<Object>} Complete menu data including categories, items, modifiers, and discounts
 */
export const getCompleteMenuByStore = async (storeId) => {
  try {
    if (!storeId) {
      throw new Error('Store ID is required');
    }


    // Fetch all menu components in parallel
    const [categories, menuItems, modifiers, discounts] = await Promise.all([
      getCategoriesByStore(storeId),
      getMenuItemsByStore(storeId),
      getModifiersByStore(storeId),
      getDiscountsByStore(storeId),
    ]);

    // Group menu items by category
    const itemsByCategory = {};
    categories.forEach(category => {
      itemsByCategory[category.id] = menuItems.filter(
        item => item.category_id === category.id
      );
    });

    // Group modifiers by menu item
    const modifiersByItem = {};
    menuItems.forEach(item => {
      if (item.modifier_ids && item.modifier_ids.length > 0) {
        modifiersByItem[item.id] = modifiers.filter(
          modifier => item.modifier_ids.includes(modifier.id)
        );
      }
    });

    const result = {
      storeId,
      categories,
      menuItems,
      modifiers,
      discounts,
      itemsByCategory,
      modifiersByItem,
      stats: {
        totalCategories: categories.length,
        totalMenuItems: menuItems.length,
        totalModifiers: modifiers.length,
        totalDiscounts: discounts.length,
      }
    };


    return result;
  } catch (error) {
    console.error('Error fetching complete menu by store:', error);
    throw error;
  }
};

/**
 * Update user's default store in Firebase
 * @param {string} userId - User ID
 * @param {string} storeId - Store ID to set as default
 * @returns {Promise<void>}
 */
export const setUserDefaultStore = async (userId, storeId) => {
  try {
    if (!userId || !storeId) {
      throw new Error('User ID and Store ID are required');
    }

    await firestore
      .doc(`users/${userId}`)
      .set(
        {
          defaultStore: storeId,
          updatedAt: new Date(),
        },
        { merge: true }
      );


  } catch (error) {
    console.error('Error setting user default store:', error);
    throw error;
  }
};

/**
 * Get user's default store
 * @param {string} userId - User ID
 * @returns {Promise<string|null>} Default store ID or null if not set
 */
export const getUserDefaultStore = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const userDoc = await firestore.doc(`users/${userId}`).get();
    
    if (userDoc.exists) {
      const userData = userDoc.data();
      return userData.defaultStore || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user default store:', error);
    throw error;
  }
};

/**
 * Filter menu items by availability and store's out-of-stock list
 * @param {Array} menuItems - Array of menu items
 * @param {Object} store - Store object with out_of_stock array
 * @returns {Array} Filtered menu items
 */
export const filterAvailableMenuItems = (menuItems, store) => {
  if (!store?.out_of_stock) {
    return menuItems;
  }

  const outOfStockItems = store.out_of_stock || [];
  const currentTime = new Date();

  // Filter out items that are currently out of stock
  const availableItems = menuItems.filter(item => {
    const outOfStockEntry = outOfStockItems.find(
      outItem => outItem.item_id === item.id
    );

    if (!outOfStockEntry) {
      return true; // Item is not in out-of-stock list
    }

    // Check if out-of-stock period has ended
    if (outOfStockEntry.out_of_stock_end_date) {
      const endDate = new Date(
        outOfStockEntry.out_of_stock_end_date.seconds * 1000 +
        outOfStockEntry.out_of_stock_end_date.nanoseconds / 1e6
      );
      return endDate <= currentTime; // Item is available if end date has passed
    }

    return false; // Item is out of stock with no end date
  });

  return availableItems;
};

export default {
  getCategoriesByStore,
  getMenuItemsByStore,
  getModifiersByStore,
  getDiscountsByStore,
  getCompleteMenuByStore,
  setUserDefaultStore,
  getUserDefaultStore,
  filterAvailableMenuItems,
};
